import dayjs from 'utils/dayjsConfig';

const MAX_AUTH_USER_LENGTH = 255;
const UNSLOTTED_PACK_LINE_FILTER = 'unslotted';

/**
 * @param {unknown} value
 * @returns {number | undefined}
 */
export function parsePositiveIntegerFilter(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const n = Number(String(value).trim());
    if (!Number.isInteger(n) || n <= 0) {
        return undefined;
    }
    return n;
}

/**
 * Converts a date value (Date, datetime-local string, or ISO string) to an ISO-8601 timestamp.
 *
 * @param {unknown} value
 * @returns {string | undefined}
 */
export function toIsoTimestamp(value) {
    if (!value) {
        return undefined;
    }
    const parsed = dayjs(value);
    if (!parsed.isValid()) {
        return undefined;
    }
    return parsed.format();
}

/**
 * Expands selected calendar days into inclusive start/end-of-day ISO bounds.
 *
 * @param {unknown} startDate
 * @param {unknown} endDate
 * @returns {{ start?: string, end?: string }}
 */
export function toDayRangeBounds(startDate, endDate) {
    const startParsed = dayjs(startDate);
    const endParsed = dayjs(endDate);
    if (!startParsed.isValid() || !endParsed.isValid()) {
        return {};
    }
    return {
        start: startParsed.startOf('day').format(),
        end: endParsed.endOf('day').format(),
    };
}

/**
 * Normalizes entity-filter TEXT values (string or string[]) to a single trimmed string.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeExternalSkuFilterValue(value) {
    const entries = Array.isArray(value) ? value : [value];
    const last = [...entries].reverse().find((entry) => entry != null && String(entry).trim() !== '');
    return last == null ? '' : String(last).trim();
}

/**
 * @param {unknown} value
 * @returns {number[]}
 */
export function normalizePositiveIntegerArray(value) {
    let entries;
    if (Array.isArray(value)) {
        entries = value;
    } else if (value == null || value === '') {
        entries = [];
    } else {
        entries = [value];
    }
    const ids = [];
    for (const entry of entries) {
        const parsed = parsePositiveIntegerFilter(entry);
        if (parsed !== undefined) {
            ids.push(parsed);
        }
    }
    return [...new Set(ids)];
}

/**
 * Validates entity-dropdown filter values and builds API filter params.
 *
 * @param {{
 *   externalSku?: string | string[],
 *   companySourceIds?: Array<number | string>,
 *   packLineNumber?: string | string[] | number,
 *   authUser?: string | string[],
 *   timestampRange?: { startDate?: Date | string, endDate?: Date | string },
 * }} [filterValues={}]
 * @returns {{
 *   appliedFilters: {
 *     externalSku?: number,
 *     companySourceIds?: number[],
 *     packLineNumber?: number,
 *     noPackLine?: boolean,
 *     authUser?: string,
 *     start?: string,
 *     end?: string,
 *   },
 *   error: string | null,
 * }}
 */
export function validateAndBuildAppliedFilters(filterValues = {}) {
    const externalSkuRaw = normalizeExternalSkuFilterValue(filterValues.externalSku);
    const externalSku = parsePositiveIntegerFilter(externalSkuRaw);
    if (externalSkuRaw && externalSku === undefined) {
        return { appliedFilters: {}, error: 'External SKU must be a positive integer' };
    }

    const companySourceIds = normalizePositiveIntegerArray(filterValues.companySourceIds);
    if (
        Array.isArray(filterValues.companySourceIds) &&
        filterValues.companySourceIds.length > 0 &&
        companySourceIds.length === 0
    ) {
        return { appliedFilters: {}, error: 'Company Source must be a positive integer' };
    }

    const packLineNumberRaw = normalizeExternalSkuFilterValue(filterValues.packLineNumber);
    const noPackLine = packLineNumberRaw.toLowerCase() === UNSLOTTED_PACK_LINE_FILTER;
    const packLineNumber = noPackLine ? undefined : parsePositiveIntegerFilter(packLineNumberRaw);
    if (packLineNumberRaw && !noPackLine && packLineNumber === undefined) {
        return {
            appliedFilters: {},
            error: 'Pack Line must be a positive integer or “unslotted”',
        };
    }

    const authUserRaw = normalizeExternalSkuFilterValue(filterValues.authUser);
    if (authUserRaw.length > MAX_AUTH_USER_LENGTH) {
        return { appliedFilters: {}, error: 'User must be at most 255 characters' };
    }
    const authUser = authUserRaw || undefined;

    const startRaw = filterValues.timestampRange?.startDate;
    const endRaw = filterValues.timestampRange?.endDate;
    const hasStart = Boolean(startRaw);
    const hasEnd = Boolean(endRaw);
    if (hasStart !== hasEnd) {
        return { appliedFilters: {}, error: 'Start and end must be supplied together' };
    }

    /** @type {{
     *   externalSku?: number,
     *   companySourceIds?: number[],
     *   packLineNumber?: number,
     *   noPackLine?: boolean,
     *   authUser?: string,
     *   start?: string,
     *   end?: string,
     * }} */
    const appliedFilters = {
        externalSku,
        ...(companySourceIds.length > 0 ? { companySourceIds } : {}),
        ...(packLineNumber !== undefined ? { packLineNumber } : {}),
        ...(noPackLine ? { noPackLine: true } : {}),
        ...(authUser ? { authUser } : {}),
    };

    if (!hasStart && !hasEnd) {
        return { appliedFilters, error: null };
    }

    const { end, start } = toDayRangeBounds(startRaw, endRaw);
    if (!start || !end) {
        return { appliedFilters: {}, error: 'Start and end must be valid dates' };
    }

    if (!dayjs(start).isBefore(dayjs(end))) {
        return { appliedFilters: {}, error: 'Start must be before end' };
    }

    return {
        appliedFilters: {
            ...appliedFilters,
            start,
            end,
        },
        error: null,
    };
}
